import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import ActionButton from "../buttons/ActionButton";
import { useActionWithPath } from "utils/query";
import { Dispatch, SetStateAction, useEffect, useState, useMemo } from "react";
import { useAuth } from "auth/AuthContext";
import { useFetch } from "utils/query";

export interface UpdateKubernetesDialogProps {
  clusterDetail: any;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  refetchCluster?: () => void;
}

function getNextVersion(
  currentVersion: string,
  availableVersions: string[]
): string {
  const current = parseFloat(currentVersion);
  const nextVersion = availableVersions.find((v) => parseFloat(v) > current);
  return nextVersion || availableVersions[availableVersions.length - 1];
}

export default function UpdateKubernetesDialog({
  clusterDetail,
  open,
  setOpen,
  refetchCluster,
}: UpdateKubernetesDialogProps) {
  const { setErrorToast, setSuccessToast } = useAuth();
  const [selectedVersion, setSelectedVersion] = useState<string>("");
  const [force, setForce] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentVersion =
    clusterDetail?.eksVersion || "Unable to determine version";

  // Fetch available EKS versions using the standard hook
  const { data: versionsData, isFetching: loadingVersions } = useFetch<{
    versions: string[];
  }>("/eksVersions", {
    enabled: open, // Only fetch when dialog is open
  });

  const availableVersions = useMemo(() => {
    return versionsData?.versions || [];
  }, [versionsData?.versions]);

  const selectableVersions = useMemo(() => {
    return availableVersions.filter(
      (version) => parseFloat(version) >= parseFloat(currentVersion)
    );
  }, [availableVersions, currentVersion]);

  // Set default version when versions are loaded
  useEffect(() => {
    if (open && availableVersions.length > 0 && !selectedVersion) {
      const defaultVersion = getNextVersion(currentVersion, availableVersions);
      setSelectedVersion(defaultVersion);
      setForce(false);
    }
  }, [open, availableVersions, currentVersion, selectedVersion]);

  const closeDialog = () => {
    setOpen(false);
    setIsSubmitting(false);
  };

  const updateKubernetesAction = useActionWithPath({
    onSuccess: () => {
      setSuccessToast(
        `Kubernetes update to ${selectedVersion} has been triggered for ${clusterDetail.name}`
      );
      closeDialog();
      refetchCluster?.();
    },
    onError: async (response: any) => {
      setIsSubmitting(false);
      try {
        const json = await response.json();
        setErrorToast(
          `Could not trigger Kubernetes update: ${
            json.message || "Unknown error"
          }`
        );
      } catch (e) {
        setErrorToast("Could not trigger Kubernetes update");
      }
      refetchCluster?.();
    },
  });

  const handleUpdateKubernetes = () => {
    if (!clusterDetail) return;

    setIsSubmitting(true);

    // @ts-ignore
    updateKubernetesAction.mutate({
      method: "post",
      path: `/updateKubernetes?clusterName=${clusterDetail.name}&version=${selectedVersion}&force=${force}`,
    });
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={closeDialog}>
      <DialogTitle>
        Update Kubernetes for <i>{clusterDetail?.name}</i>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ paddingTop: 2 }}>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Current version: <strong>{currentVersion}</strong>
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth disabled={loadingVersions}>
              <InputLabel id="versionLabel">Target Version</InputLabel>
              <Select
                value={selectedVersion}
                onChange={(event) => setSelectedVersion(event.target.value)}
                labelId="versionLabel"
                id="versionSelect"
                label="Target Version"
                startAdornment={
                  loadingVersions && <CircularProgress size={20} />
                }
              >
                {selectableVersions.map((version) => {
                  const defaultVersion = getNextVersion(
                    currentVersion,
                    availableVersions
                  );
                  return (
                    <MenuItem key={version} value={version}>
                      {version}
                      {version === defaultVersion && " (recommended)"}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={force}
                  onChange={(event) => setForce(event.target.checked)}
                />
              }
              label="Force update (skip compatibility checks)"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: "warning.light", borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This will update the EKS cluster,
                addons, and all node groups. The process may take 30-60 minutes
                and will cause brief service interruptions during node group
                updates.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={closeDialog}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <ActionButton
          variant="contained"
          onClick={handleUpdateKubernetes}
          disabled={!selectedVersion || isSubmitting || loadingVersions}
          progress={isSubmitting}
        >
          Update Kubernetes
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}
